/**
 * Image detail drawer — full metadata, Gemini rationale, hero/body
 * eligibility with reason codes, inline edit, reclassify and soft-delete.
 *
 * Shared by the /images library and the entity workbench's Images tab
 * (extracted 2026-08-13). Self-contained: it takes an imageId and callbacks,
 * runs its own queries, and leaves cache invalidation to the caller.
 */
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  AlertTriangle, CheckCircle2, ExternalLink, HelpCircle, Loader2, Save,
  Sparkles, Trash2, X,
} from 'lucide-react';
import {
  getImage, getImageHistory, updateImage, softDeleteImage, reclassifyImage,
  setImageCuration,
} from '../../lib/api/images.js';
import type {
  ImageDetail, ImageOwnership, ImageRole, ImageType, ImageUpdateRequest,
} from '../../lib/api/types.js';
import type { CurationStatus } from '../../lib/api/images.js';
import { fmtNum } from './ImageCard.js';

const IMAGE_TYPES: ImageType[] = [
  'logo', 'operational', 'personnel', 'facility',
  'chart', 'graph', 'map', 'diagram', 'cross_section',
  'photo', 'illustration', 'generated', 'other', 'uncertain',
];
const IMAGE_ROLES: ImageRole[] = ['data_figure', 'editorial_photo', 'decorative', 'logo'];
const OWNERSHIPS: ImageOwnership[] = ['proprietary', 'public_domain', 'generic', 'unknown'];

export function ImageDetailDrawer(props: {
  imageId: string;
  onClose: () => void;
  onChange: () => void;
  onDeleted: () => void;
}) {
  const { imageId, onClose, onChange, onDeleted } = props;
  const detailQuery = useQuery({
    queryKey: ['image-detail', imageId],
    queryFn: () => getImage(imageId),
  });
  const historyQuery = useQuery({
    queryKey: ['image-history', imageId],
    queryFn: () => getImageHistory(imageId, 20),
  });

  const [edit, setEdit] = useState<ImageUpdateRequest>({});
  const editEmpty = Object.keys(edit).length === 0;
  const image = detailQuery.data;

  const updateMutation = useMutation({
    mutationFn: () => updateImage(imageId, edit),
    onSuccess: () => {
      setEdit({});
      onChange();
    },
  });

  const reclassifyMutation = useMutation({
    mutationFn: () => reclassifyImage(imageId, {}),
    onSuccess: () => onChange(),
  });

  const deleteMutation = useMutation({
    mutationFn: () => softDeleteImage(imageId),
    onSuccess: () => onDeleted(),
  });

  // Operator triage for themeless imagery (cynapse mig 060). cynapse
  // recomputes eligibility on write, so refetching the detail query is what
  // makes the reason codes below reflect the decision just made.
  const curationMutation = useMutation({
    mutationFn: (status: CurationStatus) => setImageCuration(imageId, status),
    onSuccess: () => {
      detailQuery.refetch();
      onChange();
    },
  });

  const setField = <K extends keyof ImageUpdateRequest>(
    key: K,
    value: ImageUpdateRequest[K] | undefined,
  ) => {
    setEdit((prev) => {
      const next = { ...prev };
      if (value === undefined || value === '') delete next[key];
      else (next as any)[key] = value;
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full max-w-2xl bg-surface shadow-2xl overflow-y-auto">
        <header className="sticky top-0 bg-surface border-b border-gray-200 p-4 flex items-center justify-between z-10">
          <h2 className="font-semibold text-gray-900 text-lg truncate">
            Image {imageId.slice(0, 8)}…
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </header>

        {detailQuery.isLoading && (
          <div className="p-6 flex items-center gap-2 text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        )}

        {image && (
          <div className="p-4 space-y-4">
            <div className="bg-gray-100 rounded overflow-hidden">
              <img
                src={`https://storage.googleapis.com/${image.gcs_bucket}/${image.gcs_path}`}
                alt={image.alt_text ?? ''}
                className="w-full max-h-96 object-contain bg-surface"
              />
            </div>

            <KvBlock title="Classification" rows={[
              ['image_type', image.image_type],
              ['image_role', image.image_role ?? '—'],
              ['ownership_type', image.ownership_type ?? '—'],
              ['quality_score', fmtNum(image.quality_score)],
              ['classifier_confidence', fmtNum(image.classifier_confidence)],
              ['low_confidence', image.low_confidence ? 'YES' : 'no'],
              ['classifier_version', image.classifier_version ?? '—'],
              ['classified_at', image.classified_at ? format(new Date(image.classified_at), 'PP p') : '—'],
            ]} />

            <EligibilityPanel image={image} />

            <CurationPanel
              image={image}
              onSet={(status) => curationMutation.mutate(status)}
              pending={curationMutation.isPending}
            />

            {image.classifier_rationale && (
              <section>
                <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Rationale</h3>
                <p className="text-sm text-gray-800 bg-yellow-50 border border-yellow-200 rounded p-2 whitespace-pre-wrap">
                  {image.classifier_rationale}
                </p>
              </section>
            )}

            <KvBlock title="Source" rows={[
              ['source_type', image.source_type],
              ['source_entity_id', image.source_entity_id ?? '—'],
              ['source_announcement_id', image.source_announcement_id ?? '—'],
              ['original_url', image.original_url ?? '—'],
              ['copyright_holder', image.copyright_holder ?? '—'],
              ['license_type', image.license_type ?? '—'],
              ['created_at', format(new Date(image.created_at), 'PP p')],
            ]} />

            <EditCard
              image={image}
              edit={edit}
              onField={setField}
              onSave={() => updateMutation.mutate()}
              onCancel={() => setEdit({})}
              saving={updateMutation.isPending}
              saveError={(updateMutation.error as Error | null)?.message}
              disabled={editEmpty}
            />

            <section>
              <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2">AI description (raw)</h3>
              <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto max-h-64">
                {image.ai_description ? JSON.stringify(image.ai_description, null, 2) : '—'}
              </pre>
            </section>

            {historyQuery.data && historyQuery.data.history.length > 0 && (
              <section>
                <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                  Classification history ({historyQuery.data.history.length})
                </h3>
                <ul className="space-y-2">
                  {historyQuery.data.history.map((h) => (
                    <li key={h.history_id} className="text-xs border border-gray-200 rounded p-2">
                      <div className="flex justify-between mb-1">
                        <span className="font-medium">{h.classifier_version}</span>
                        <span className="text-gray-500">{format(new Date(h.classified_at), 'PP p')}</span>
                      </div>
                      <div className="text-gray-700">
                        type=<b>{h.image_type ?? '—'}</b> · role=<b>{h.image_role ?? '—'}</b> · own=<b>{h.ownership_type ?? '—'}</b>
                        {h.low_confidence && <span className="ml-2 text-yellow-700">low_conf</span>}
                        <span className="ml-2 text-gray-500">by {h.actor}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="border-t border-gray-200 pt-4 flex flex-wrap gap-2">
              <button
                onClick={() => reclassifyMutation.mutate()}
                disabled={reclassifyMutation.isPending}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {reclassifyMutation.isPending
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Sparkles className="h-4 w-4" />}
                Reclassify with Gemini
              </button>
              <button
                onClick={() => {
                  if (!confirm('Soft-delete this image? It will be hidden from publishing but its row + GCS object are retained.')) return;
                  deleteMutation.mutate();
                }}
                disabled={deleteMutation.isPending}
                className="px-3 py-1.5 bg-red-600 text-white rounded text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {deleteMutation.isPending
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Trash2 className="h-4 w-4" />}
                Soft-delete
              </button>
              <a
                href={`https://storage.googleapis.com/${image.gcs_bucket}/${image.gcs_path}`}
                target="_blank"
                rel="noreferrer"
                className="ml-auto px-3 py-1.5 border border-gray-300 rounded text-sm flex items-center gap-1 text-gray-700"
              >
                <ExternalLink className="h-4 w-4" />
                Open in GCS
              </a>
            </section>

            {reclassifyMutation.error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                Reclassify failed: {(reclassifyMutation.error as Error).message}
              </div>
            )}
            {deleteMutation.error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                Delete failed: {(deleteMutation.error as Error).message}
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function KvBlock(props: { title: string; rows: [string, React.ReactNode][] }) {
  return (
    <section>
      <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-1">{props.title}</h3>
      <dl className="grid grid-cols-[10rem_1fr] gap-y-1 gap-x-3 text-sm">
        {props.rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-gray-500">{k}</dt>
            <dd className="text-gray-900 break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

// ----------------------------------------------------------------------------
// M1 B.9 — "Why not hero?" / "Why not body?" eligibility panel.
//
// Reads is_hero_eligible / is_body_eligible / eligibility_reasons from cynapse
// migration 014, plus the three visual signals (brightness, contrast,
// Laplacian-variance sharpness) that drive most blocks. Surfaces the actual
// reason codes the operator needs to (a) understand why an image was
// suppressed and (b) decide whether to tune the thresholds in image_eligibility.py.
//
// Reason-code mapping is intentionally explicit so a renamed code (which
// the B.2 module forbids — keep stable) doesn't silently render as a raw
// snake_case token.
// ----------------------------------------------------------------------------

const REASON_LABELS: Record<string, string> = {
  brightness_too_low:   'Too dark (near-black)',
  brightness_too_high:  'Too bright / washed-out',
  contrast_too_low:     'Low contrast / flat',
  sharpness_too_low:    'Blurry — lacks edge detail',
  quality_too_low:      'Below quality floor',
  composition_too_low:  'Aspect ratio does not suit hero (cropping / distortion)',
  role_not_eligible:    'Editorial role does not qualify',
  role_person_portrait: 'People imagery — headshots and board slides are excluded from articles',
  caption_degenerate:   'Caption is boilerplate ("Figure 1", "ASX:XYZ", etc.)',
  caption_regulatory:   'Caption is a regulatory-disclosure table',
  page_too_late:        'Page > 30 in source PDF',
  metrics_missing:      'Visual metrics not yet computed (B.5 backfill pending)',
  resolution_too_low:   'Too few pixels for this slot',
  crop_retention_too_low: 'Hero crop would discard most of the image (page-shaped or extreme aspect)',
  text_density_too_high:  'Too much text to stand alone — reads as a slide or document page',
  // Document-page composites (cynapse 0.12.0, 2026-08-21). These shipped
  // without labels and rendered as raw codes in this panel until 2026-08-26.
  document_page_composite:
    'The image is an entire document page — letterhead, prose and footer, not a picture',
  full_page_render_uncropped:
    'Portrait page render the figure crop could not resolve — treated as a document page until proven otherwise',
  // Social crop retention (2026-08-26). Distinct from crop_retention_too_low:
  // different tier, different target aspect (1.91:1 vs the hero's 3:2).
  social_crop_retention_too_low:
    'Social card would keep only a horizontal band from the middle of a portrait image',
  // Distinct from document_page_composite on purpose: that one means the model
  // said yes, this one means nobody has asked yet. Reclassify to resolve it.
  document_page_unverified:
    'Page-shaped scan never checked for being a whole document page — reclassify to confirm or clear it',
  // Themeless sources — images scraped from a company website, which arrive
  // with no story context (cynapse mig 060, operator decisions 2026-08-18/19).
  themeless_contains_people:
    'People in a website image — external imagery must be people-free (incidental figures are allowed only in aerial or facility shots)',
  themeless_subject_not_allowed:
    'Subject not permitted from a website source — only operating assets, facilities, aerial site views and product detail qualify',
  themeless_awaiting_curation:
    'Needs operator approval — renders are never published automatically, and a rejected image stays rejected',
  source_identity_only:
    'Leadership/board imagery — kept for entity identity, never used in articles',
};

const SUBJECT_LABELS: Record<string, string> = {
  operating_asset:   'Operating asset',
  facility_exterior: 'Facility exterior',
  aerial_site:       'Aerial / site view',
  product_equipment: 'Product or equipment',
  project_render:    'Render (unbuilt)',
  event:             'Event',
  people:            'People',
  generic_stock:     'Generic stock',
  brand_marketing:   'Brand / marketing',
  other:             'Other',
};

const PEOPLE_LABELS: Record<string, string> = {
  none:       'No people',
  incidental: 'Incidental people',
  prominent:  'People are the subject',
};

/**
 * Operator triage for themeless (scraped) imagery — cynapse mig 060.
 *
 * Renders only for externally-sourced images: a filing figure has no
 * subject_class and nothing to triage, so showing an empty panel on every PDF
 * image would be noise.
 *
 * The approve control exists because some subjects — renders of unbuilt
 * projects — may never flow automatically but are legitimately publishable on
 * a story genuinely about that development. Rejection always wins over the
 * automatic signals, so it is available even on images that already pass.
 */
function CurationPanel({
  image,
  onSet,
  pending,
}: {
  image: ImageDetail;
  onSet: (status: CurationStatus) => void;
  pending: boolean;
}) {
  const isThemeless = !!image.subject_class || !!image.contains_people;
  if (!isThemeless) return null;

  const status = image.curation_status ?? null;
  const subject = image.subject_class
    ? SUBJECT_LABELS[image.subject_class] ?? image.subject_class
    : '—';
  const people = image.contains_people
    ? PEOPLE_LABELS[image.contains_people] ?? image.contains_people
    : '—';

  const chip = (label: string, tone: string) => (
    <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${tone}`}>{label}</span>
  );

  return (
    <section className="border border-gray-200 rounded p-3 space-y-2">
      <h3 className="text-xs uppercase tracking-wide text-gray-500">
        External image — operator triage
      </h3>

      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
        {chip(subject, 'bg-gray-100 text-gray-800')}
        {chip(
          people,
          image.contains_people === 'none'
            ? 'bg-green-100 text-green-800'
            : image.contains_people === 'incidental'
              ? 'bg-amber-100 text-amber-800'
              : 'bg-red-100 text-red-800',
        )}
        {image.source_area && chip(image.source_area, 'bg-blue-50 text-blue-700')}
      </div>

      {image.source_page_url && (
        <a
          href={image.source_page_url}
          target="_blank"
          rel="noreferrer"
          className="block text-xs text-blue-600 hover:underline truncate"
          title={image.source_page_url}
        >
          {image.source_page_url}
        </a>
      )}

      <div className="flex items-center gap-2 pt-1">
        <span className="text-xs text-gray-500">
          {status ? `Status: ${status}` : 'Not reviewed'}
          {image.curated_by ? ` · ${image.curated_by}` : ''}
        </span>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          disabled={pending || status === 'approved'}
          onClick={() => onSet('approved')}
          className="px-2 py-1 text-xs rounded bg-green-600 text-white disabled:opacity-40"
        >
          Approve
        </button>
        <button
          type="button"
          disabled={pending || status === 'rejected'}
          onClick={() => onSet('rejected')}
          className="px-2 py-1 text-xs rounded bg-red-600 text-white disabled:opacity-40"
        >
          Reject
        </button>
        {status && (
          <button
            type="button"
            disabled={pending}
            onClick={() => onSet(null)}
            className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 disabled:opacity-40"
          >
            Clear
          </button>
        )}
        {pending && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
      </div>
    </section>
  );
}

function EligibilityPanel({ image }: { image: ImageDetail }) {
  const hero = image.is_hero_eligible;
  const body = image.is_body_eligible;
  const reasons = image.eligibility_reasons ?? null;
  const heroBlocked = reasons?.hero_blocked ?? [];
  const bodyBlocked = reasons?.body_blocked ?? [];

  // null = not yet computed by cynapse (pre-backfill row); boolean = explicit verdict.
  const pendingBackfill = !image.visual_metrics_at;

  const statusBadge = (val: boolean | null | undefined) => {
    if (val === true) {
      return (
        <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium">
          <CheckCircle2 className="h-3.5 w-3.5" /> Eligible
        </span>
      );
    }
    if (val === false) {
      return (
        <span className="inline-flex items-center gap-1 text-red-700 text-xs font-medium">
          <AlertTriangle className="h-3.5 w-3.5" /> Blocked
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-gray-500 text-xs font-medium">
        <HelpCircle className="h-3.5 w-3.5" /> Pending backfill
      </span>
    );
  };

  const renderBlocked = (codes: string[]) => {
    if (codes.length === 0) return null;
    return (
      <ul className="mt-1 space-y-0.5 text-xs text-gray-700">
        {codes.map((code) => (
          <li key={code} className="flex items-start gap-1.5">
            <span className="mt-1 inline-block h-1 w-1 flex-shrink-0 rounded-full bg-red-500" />
            <span>
              <span className="text-red-700 font-medium">
                {REASON_LABELS[code] ?? code}
              </span>
              <span className="text-gray-400 ml-1">({code})</span>
            </span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section>
      <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-1">
        Hero / body eligibility
      </h3>
      <div className="border border-gray-200 rounded p-3 space-y-3 text-sm">
        <div className="grid grid-cols-[6rem_1fr] gap-y-2 items-center">
          <span className="text-gray-500 text-xs">Hero</span>
          <div>
            {statusBadge(hero)}
            {hero === false && renderBlocked(heroBlocked)}
          </div>
          <span className="text-gray-500 text-xs">Body figure</span>
          <div>
            {statusBadge(body)}
            {body === false && renderBlocked(bodyBlocked)}
          </div>
        </div>

        <div className="border-t border-gray-100 pt-2">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Visual metrics
          </div>
          {pendingBackfill ? (
            <p className="text-xs text-gray-500">
              Not yet computed. Run the "Backfill Visual Metrics &amp; Eligibility"
              tool to populate.
            </p>
          ) : (
            <dl className="grid grid-cols-[8rem_1fr] gap-y-0.5 text-xs">
              <dt className="text-gray-500">brightness</dt>
              <dd className="text-gray-900">
                {fmtNum(image.visual_brightness, 1)}
                <span className="text-gray-400 ml-2">/ 255</span>
              </dd>
              <dt className="text-gray-500">contrast</dt>
              <dd className="text-gray-900">
                {fmtNum(image.visual_contrast, 1)}
                <span className="text-gray-400 ml-2">stddev</span>
              </dd>
              <dt className="text-gray-500">sharpness</dt>
              <dd className="text-gray-900">
                {fmtNum(image.visual_sharpness_laplacian, 1)}
                <span className="text-gray-400 ml-2">Laplacian variance</span>
              </dd>
              <dt className="text-gray-500">computed_at</dt>
              <dd className="text-gray-900">
                {image.visual_metrics_at
                  ? format(new Date(image.visual_metrics_at), 'PP p')
                  : '—'}
              </dd>
            </dl>
          )}
        </div>
      </div>
    </section>
  );
}

function EditCard(props: {
  image: ImageDetail;
  edit: ImageUpdateRequest;
  onField: <K extends keyof ImageUpdateRequest>(k: K, v: ImageUpdateRequest[K] | undefined) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  saveError?: string;
  disabled: boolean;
}) {
  const { image, edit, onField, onSave, onCancel, saving, saveError, disabled } = props;
  const get = <K extends keyof ImageUpdateRequest>(key: K): string =>
    (edit[key] as string | undefined) ?? ((image[key as keyof ImageDetail] as string | null | undefined) ?? '');

  return (
    <section className="border border-gray-200 rounded p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-wide text-gray-500">Edit</h3>
        {!disabled && (
          <span className="text-xs text-blue-700">
            {Object.keys(edit).length} pending change{Object.keys(edit).length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <label>
          <span className="text-xs text-gray-600">image_type</span>
          <select
            value={get('image_type')}
            onChange={(e) => onField('image_type', (e.target.value || undefined) as ImageType | undefined)}
            className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded"
          >
            {IMAGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>
          <span className="text-xs text-gray-600">image_role</span>
          <select
            value={get('image_role')}
            onChange={(e) => onField('image_role', (e.target.value || undefined) as ImageRole | undefined)}
            className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded"
          >
            <option value="">—</option>
            {IMAGE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label>
          <span className="text-xs text-gray-600">ownership_type</span>
          <select
            value={get('ownership_type')}
            onChange={(e) => onField('ownership_type', (e.target.value || undefined) as ImageOwnership | undefined)}
            className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded"
          >
            <option value="">—</option>
            {OWNERSHIPS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label>
          <span className="text-xs text-gray-600">quality_score (0..1)</span>
          <input
            type="number"
            step={0.05}
            min={0}
            max={1}
            value={get('quality_score')}
            onChange={(e) => onField('quality_score', e.target.value === '' ? undefined : Number(e.target.value))}
            className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded"
          />
        </label>
        <label className="col-span-2">
          <span className="text-xs text-gray-600">alt_text</span>
          <input
            type="text"
            value={get('alt_text')}
            onChange={(e) => onField('alt_text', e.target.value || undefined)}
            className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded"
          />
        </label>
        <label className="col-span-2">
          <span className="text-xs text-gray-600">caption</span>
          <input
            type="text"
            value={get('caption')}
            onChange={(e) => onField('caption', e.target.value || undefined)}
            className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded"
          />
        </label>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          onClick={onSave}
          disabled={disabled || saving}
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save changes
        </button>
        <button
          onClick={onCancel}
          disabled={disabled}
          className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-50"
        >
          Reset
        </button>
        {saveError && (
          <span className="text-sm text-red-700 self-center">{saveError}</span>
        )}
      </div>
    </section>
  );
}
